// User Authentication Service with Token Refresh
import { API_CONFIG } from '../config/env';

const API_BASE_URL = `${API_CONFIG.BASE_URL}/user`;

class UserAuthService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${this.baseURL}/refresh-token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      if (data.success && data.data.access_token) {
        // Update the access token
        localStorage.setItem('authToken', data.data.access_token);
        return data.data.access_token;
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      if (window.location.pathname !== '/signin') {
        window.location.href = '/signin';
      }
      throw error;
    }
  }

  // Make authenticated API call with automatic token refresh
  async userApiCall(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Don't override Content-Type if it's FormData (for file uploads)
    const isFormData = options.body instanceof FormData;
    const defaultHeaders = this.getAuthHeaders();
    
    // Remove Content-Type from default headers if FormData (browser will set it with boundary)
    if (isFormData) {
      delete defaultHeaders['Content-Type'];
    }
    
    const mergedHeaders = {
      ...defaultHeaders,
      ...(options.headers || {})
    };
    
    const config = {
      ...options,
      headers: mergedHeaders
    };

    try {
      const response = await fetch(url, config);
      
      // If 401 Unauthorized, try to refresh token
      if (response.status === 401) {
        try {
          await this.refreshToken();
          // Retry the request with NEW token (get fresh headers)
          const newHeaders = this.getAuthHeaders();
          if (isFormData) {
            delete newHeaders['Content-Type'];
          }
          const retryHeaders = {
            ...newHeaders,
            ...(options.headers || {})
          };
          const retryConfig = {
            ...options,
            headers: retryHeaders
          };
          const retryResponse = await fetch(url, retryConfig);
          return retryResponse;
        } catch (refreshError) {
          throw new Error('Authentication failed. Please log in again.');
        }
      }

      return response;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check if the server is running.');
      }
      throw error;
    }
  }
}

export default new UserAuthService();
